/**
 * Réglages d'un paquet principal (design system, EcranReglagesDuPaquet ; spécification §3).
 * Chaque réglage s'enregistre dès qu'il change ; « Rétablir » remet la valeur par défaut.
 */
import { useEffect, useState } from 'react';
import { avertissementFenetreDebordement } from '../../paquets/avertissement-fenetre';
import {
  REGLAGES_PAR_DEFAUT,
  seuilAlerteEffectif,
  type OrdreCartes,
  type ReglagesPaquet,
} from '../../paquets/reglages';
import { Chargement } from '../composants/Chargement';
import { CommandeSegmentee } from '../composants/CommandeSegmentee';
import { EtatVide } from '../composants/EtatVide';
import { ListeDeroulante } from '../composants/ListeDeroulante';
import { useNotifier } from '../composants/Notifications';
import {
  ChampDuree,
  ChampNombre,
  dureeDepuisMinutes,
  GroupeDeReglages,
  Interrupteur,
  LigneDeReglage,
  Onglets,
  ReglagesDependants,
  texteDuree,
} from '../composants/Reglages';
import { TableauDesEtapes } from '../composants/TableauDesEtapes';
import { Coquille } from '../Coquille';
import { useDonnees } from '../donnees/contexte';
import { messageErreur } from '../donnees/erreurs';
import { nombre, pluriel } from '../outils';
import { naviguer, type OngletReglagesPaquet } from '../routes';

const ORDRES: { valeur: OrdreCartes; libelle: string; description: string }[] = [
  {
    valeur: 'chronologique',
    libelle: 'Chronologique',
    description:
      'Nouvelles cartes et cartes dues dans l’ordre d’ajout, les plus anciennes d’abord.',
  },
  {
    valeur: 'hasard',
    libelle: 'Au hasard',
    description: 'Nouvelles cartes et cartes dues piochées et présentées au hasard.',
  },
  {
    valeur: 'probabilite_rappel',
    libelle: 'Probabilité de rappel croissante',
    description:
      'Les cartes les plus près d’être oubliées d’abord ; les nouvelles cartes sont exclues.',
  },
  {
    valeur: 'toutes_chronologique',
    libelle: 'Toutes les cartes, ordre chronologique',
    description:
      'Toutes les cartes du paquet, dues ou non, dans l’ordre d’ajout ; limites quotidiennes ignorées.',
  },
];

const D = REGLAGES_PAR_DEFAUT;
const pourcent = (p: number) => `${nombre(Math.round(p * 100))} %`;
const ans = (jours: number) => Math.max(1, Math.round(jours / 365));

export function EcranReglagesDuPaquet({
  paquetId,
  onglet = 'seance',
}: {
  paquetId: string;
  onglet?: OngletReglagesPaquet;
}) {
  const { depot, paquets, rechargerJours } = useDonnees();
  const notifier = useNotifier();
  const [reglages, setReglages] = useState<ReglagesPaquet | null>(null);
  const [revisions, setRevisions] = useState<number | null>(null);
  const [introuvable, setIntrouvable] = useState(false);
  const paquet = paquets?.find((p) => p.id === paquetId);

  useEffect(() => {
    let actif = true;
    setReglages(null);
    void depot
      .reglages(paquetId)
      .then((r) => actif && setReglages(r))
      .catch(() => actif && setIntrouvable(true));
    void depot
      .revisionsDuPaquet(paquetId, new Date(0))
      .then((r) => actif && setRevisions(r.length))
      .catch(() => undefined);
    return () => {
      actif = false;
    };
  }, [depot, paquetId]);

  async function modifier(modification: Partial<ReglagesPaquet>) {
    if (!reglages) return;
    const avant = reglages;
    setReglages({ ...reglages, ...modification });
    try {
      await depot.modifierReglages(paquetId, modification);
      void rechargerJours();
    } catch (e) {
      setReglages(avant);
      notifier({ message: messageErreur(e), statut: 'danger' });
    }
  }

  if (introuvable || (paquets !== null && (!paquet || paquet.parentId !== null))) {
    return (
      <Coquille titre="Réglages du paquet" colonne="formulaire">
        <EtatVide
          icone="settings"
          titre="Réglages introuvables"
          texte="Seul un paquet principal porte des réglages ; ses sous-paquets suivent les siens."
          style={{ paddingTop: 'var(--espace-48)' }}
        />
      </Coquille>
    );
  }

  if (!reglages || !paquet) {
    return (
      <Coquille titre="Réglages du paquet" colonne="formulaire">
        <Chargement type="rotation" texte="Chargement des réglages…" centre />
      </Coquille>
    );
  }

  const r = reglages;
  const probabilite = r.ordreCartes === 'probabilite_rappel';
  const toutes = r.ordreCartes === 'toutes_chronologique';
  const horsFenetre = avertissementFenetreDebordement(r);
  const dureesHorsFenetre = [...new Set(horsFenetre.map((d) => d.minutes))]
    .sort((a, b) => a - b)
    .map((m) => texteDuree(dureeDepuisMinutes(m)));

  const panneaux = {
    seance: (
      <GroupeDeReglages titre="Séance">
        <LigneDeReglage
          libelle="Afficher le fil d’Ariane"
          description="Pendant la révision, montre où se trouve la carte (paquet principal › sous-paquets)."
          modifiee={r.afficherFilAriane !== D.afficherFilAriane}
          valeurParDefaut="activé"
          onRetablir={() => void modifier({ afficherFilAriane: D.afficherFilAriane })}
          commande={(ids) => (
            <Interrupteur
              id={ids.idCommande}
              libelleId={ids.libelleId}
              descriptionId={ids.descriptionId}
              allume={r.afficherFilAriane}
              onChange={(v) => void modifier({ afficherFilAriane: v })}
            />
          )}
        />
        <LigneDeReglage
          libelle="Ordre des cartes"
          description={`${ORDRES.find((o) => o.valeur === r.ordreCartes)?.description ?? ''} N’influence pas la planification FSRS.`}
          modifiee={r.ordreCartes !== D.ordreCartes}
          valeurParDefaut="Au hasard"
          onRetablir={() => void modifier({ ordreCartes: D.ordreCartes })}
          commande={(ids) => (
            <ListeDeroulante
              libelleId={ids.libelleId}
              options={ORDRES}
              valeur={r.ordreCartes}
              onChoisir={(v) => void modifier({ ordreCartes: v })}
            />
          )}
        />
        {probabilite && (
          <LigneDeReglage
            libelle="Seuil d’alerte"
            description="La phase 1 comprend les cartes dont la probabilité de rappel est sous ce seuil ; une alerte s’affiche après la dernière. Par défaut : l’objectif de mémorisation."
            modifiee={r.seuilAlerte !== null}
            valeurParDefaut={`objectif de mémorisation (${pourcent(r.objectifMemorisation)})`}
            onRetablir={() => void modifier({ seuilAlerte: null })}
            commande={(ids) => (
              <ChampNombre
                id={ids.idCommande}
                libelleId={ids.libelleId}
                descriptionId={ids.descriptionId}
                valeur={Math.round(seuilAlerteEffectif(r) * 100)}
                min={1}
                max={99}
                unite="%"
                onChange={(v) => void modifier({ seuilAlerte: v / 100 })}
              />
            )}
          />
        )}
        <LigneDeReglage
          libelle="Nouvelles cartes par jour"
          description={
            probabilite
              ? 'Sans effet en ordre par probabilité de rappel : les nouvelles cartes en sont exclues.'
              : toutes
                ? 'Sans effet en ordre « toutes les cartes » : les limites quotidiennes sont ignorées.'
                : 'Cartes jamais vues introduites chaque jour.'
          }
          modifiee={r.nouvellesCartesParJour !== D.nouvellesCartesParJour}
          valeurParDefaut={pluriel(D.nouvellesCartesParJour, 'carte')}
          onRetablir={() => void modifier({ nouvellesCartesParJour: D.nouvellesCartesParJour })}
          commande={(ids) => (
            <ChampNombre
              id={ids.idCommande}
              libelleId={ids.libelleId}
              descriptionId={ids.descriptionId}
              valeur={r.nouvellesCartesParJour}
              min={0}
              max={9999}
              unite={{ singulier: 'carte', pluriel: 'cartes' }}
              desactive={probabilite || toutes}
              onChange={(v) => void modifier({ nouvellesCartesParJour: v })}
            />
          )}
        />
        <LigneDeReglage
          libelle="Révisions maximum par jour"
          description={
            toutes
              ? 'Sans effet en ordre « toutes les cartes » : les limites quotidiennes sont ignorées.'
              : 'Plafond des cartes acquises à revoir chaque jour. Les cartes en apprentissage ne sont pas limitées.'
          }
          modifiee={r.revisionsMaxParJour !== D.revisionsMaxParJour}
          valeurParDefaut={pluriel(D.revisionsMaxParJour, 'carte')}
          onRetablir={() => void modifier({ revisionsMaxParJour: D.revisionsMaxParJour })}
          commande={(ids) => (
            <ChampNombre
              id={ids.idCommande}
              libelleId={ids.libelleId}
              descriptionId={ids.descriptionId}
              valeur={r.revisionsMaxParJour}
              min={0}
              max={9999}
              unite={{ singulier: 'carte', pluriel: 'cartes' }}
              desactive={toutes}
              onChange={(v) => void modifier({ revisionsMaxParJour: v })}
            />
          )}
        />
        <LigneDeReglage
          libelle="Retournement automatique"
          description="Retourne la carte au bout du délai choisi après son affichage, sans la noter."
          modifiee={r.retournementAutoSecondes !== null}
          valeurParDefaut="désactivé"
          onRetablir={() => void modifier({ retournementAutoSecondes: null })}
          commande={(ids) => (
            <Interrupteur
              id={ids.idCommande}
              libelleId={ids.libelleId}
              descriptionId={ids.descriptionId}
              allume={r.retournementAutoSecondes !== null}
              onChange={(v) => void modifier({ retournementAutoSecondes: v ? 10 : null })}
            />
          )}
        />
        <ReglagesDependants desactives={r.retournementAutoSecondes === null}>
          <LigneDeReglage
            libelle="Délai avant le retournement"
            description="Compté depuis l’affichage de la question."
            commande={(ids) => (
              <ChampNombre
                id={ids.idCommande}
                libelleId={ids.libelleId}
                descriptionId={ids.descriptionId}
                valeur={r.retournementAutoSecondes ?? 10}
                min={1}
                max={3600}
                unite="s"
                desactive={r.retournementAutoSecondes === null}
                onChange={(v) => void modifier({ retournementAutoSecondes: v })}
              />
            )}
          />
          <LigneDeReglage
            libelle="Affichage du minuteur"
            description="Compte à rebours numérique, camembert qui se vide, ou masqué (le minuteur tourne sans être affiché)."
            modifiee={r.affichageMinuteur !== D.affichageMinuteur}
            valeurParDefaut="Numérique"
            onRetablir={() => void modifier({ affichageMinuteur: D.affichageMinuteur })}
            commande={(ids) => (
              <CommandeSegmentee
                etiquettePar={ids.libelleId}
                taille="petite"
                options={[
                  { valeur: 'numerique', libelle: 'Numérique' },
                  { valeur: 'camembert', libelle: 'Camembert' },
                  { valeur: 'masque', libelle: 'Masqué' },
                ]}
                valeur={r.affichageMinuteur}
                desactivee={r.retournementAutoSecondes === null}
                onChoisir={(v) => void modifier({ affichageMinuteur: v })}
              />
            )}
          />
        </ReglagesDependants>
      </GroupeDeReglages>
    ),
    etapes: (
      <>
        <section className="fc-ligne-de-reglage-groupe" aria-labelledby="etapes-apprentissage">
          <header className="fc-ligne-de-reglage-groupe__entete">
            <h2 className="fc-ligne-de-reglage-groupe__titre" id="etapes-apprentissage">
              Étapes d’apprentissage
            </h2>
            <p className="fc-ligne-de-reglage-groupe__description">
              Passages rapprochés d’une nouvelle carte avant qu’elle devienne acquise.
            </p>
          </header>
          <TableauDesEtapes
            liste="apprentissage"
            idTitre="etapes-apprentissage"
            valeur={r.etapesApprentissage}
            onChange={(v) => void modifier({ etapesApprentissage: v })}
          />
        </section>
        <section className="fc-ligne-de-reglage-groupe" aria-labelledby="etapes-reapprentissage">
          <header className="fc-ligne-de-reglage-groupe__entete">
            <h2 className="fc-ligne-de-reglage-groupe__titre" id="etapes-reapprentissage">
              Étapes de réapprentissage
            </h2>
            <p className="fc-ligne-de-reglage-groupe__description">
              Passages rapprochés d’une carte acquise oubliée (notée 1 (À revoir)) avant qu’elle
              redevienne acquise.
            </p>
          </header>
          <TableauDesEtapes
            liste="reapprentissage"
            idTitre="etapes-reapprentissage"
            valeur={r.etapesReapprentissage}
            onChange={(v) => void modifier({ etapesReapprentissage: v })}
          />
        </section>
        <GroupeDeReglages titre="Retour des cartes en apprentissage">
          <LigneDeReglage
            libelle="Fenêtre de débordement"
            description="Les cartes en apprentissage qui reviennent dans moins de cette durée restent dans la séance."
            modifiee={r.fenetreDebordementMinutes !== D.fenetreDebordementMinutes}
            valeurParDefaut={texteDuree(dureeDepuisMinutes(D.fenetreDebordementMinutes))}
            onRetablir={() =>
              void modifier({ fenetreDebordementMinutes: D.fenetreDebordementMinutes })
            }
            {...(dureesHorsFenetre.length > 0
              ? {
                  message: {
                    ton: 'erreur' as const,
                    texte: `Plus courte que ${dureesHorsFenetre.join(', ').replace(/, ([^,]+)$/, ' et $1')} : les cartes notées avec ces boutons ne reviendront pas dans cette séance et seront vues à la séance suivante.`,
                  },
                }
              : {})}
            commande={(ids) => (
              <ChampDuree
                id={ids.idCommande}
                libelleId={ids.libelleId}
                descriptionId={ids.descriptionId}
                minutes={r.fenetreDebordementMinutes}
                erreur={dureesHorsFenetre.length > 0}
                onChange={(m) => void modifier({ fenetreDebordementMinutes: m })}
              />
            )}
          />
        </GroupeDeReglages>
      </>
    ),
    planification: (
      <>
        <GroupeDeReglages titre="Planification">
          <LigneDeReglage
            libelle="Objectif de mémorisation"
            description="Probabilité visée de se souvenir d’une carte au moment où elle revient."
            modifiee={r.objectifMemorisation !== D.objectifMemorisation}
            valeurParDefaut={pourcent(D.objectifMemorisation)}
            onRetablir={() => void modifier({ objectifMemorisation: D.objectifMemorisation })}
            commande={(ids) => (
              <ChampNombre
                id={ids.idCommande}
                libelleId={ids.libelleId}
                descriptionId={ids.descriptionId}
                valeur={Math.round(r.objectifMemorisation * 100)}
                min={70}
                max={99}
                unite="%"
                onChange={(v) => void modifier({ objectifMemorisation: v / 100 })}
              />
            )}
          />
          <LigneDeReglage
            libelle="Intervalle maximum"
            description="Délai maximal avant qu’une carte revienne."
            modifiee={r.intervalleMaximumJours !== D.intervalleMaximumJours}
            valeurParDefaut="100 ans"
            onRetablir={() => void modifier({ intervalleMaximumJours: D.intervalleMaximumJours })}
            commande={(ids) => (
              <ChampNombre
                id={ids.idCommande}
                libelleId={ids.libelleId}
                descriptionId={ids.descriptionId}
                valeur={ans(r.intervalleMaximumJours)}
                min={1}
                max={100}
                unite={{ singulier: 'an', pluriel: 'ans' }}
                onChange={(v) => void modifier({ intervalleMaximumJours: v * 365 })}
              />
            )}
          />
          <LigneDeReglage
            libelle="Heure de début de journée"
            description="Heure à laquelle « aujourd’hui » commence pour ce paquet : une révision faite avant compte pour la veille."
            modifiee={r.heureDebutJournee !== D.heureDebutJournee}
            valeurParDefaut={`${D.heureDebutJournee} h`}
            onRetablir={() => void modifier({ heureDebutJournee: D.heureDebutJournee })}
            commande={(ids) => (
              <ChampNombre
                id={ids.idCommande}
                libelleId={ids.libelleId}
                descriptionId={ids.descriptionId}
                valeur={r.heureDebutJournee}
                min={0}
                max={23}
                unite="h"
                heure
                boucle
                onChange={(v) => void modifier({ heureDebutJournee: v })}
              />
            )}
          />
        </GroupeDeReglages>
        <GroupeDeReglages titre="Optimisation">
          <LigneDeReglage
            libelle="Optimisation des paramètres FSRS"
            description={`Ajuste FSRS à votre mémoire à partir de l’historique de ${paquet.nom}${
              revisions !== null ? ` (${pluriel(revisions, 'révision')})` : ''
            }. Pas encore disponible : l’outil d’optimisation reste à choisir.`}
            commande={(ids) => (
              <Interrupteur
                id={ids.idCommande}
                libelleId={ids.libelleId}
                descriptionId={ids.descriptionId}
                allume={r.optimisationFsrs}
                desactive
                onChange={() => undefined}
              />
            )}
          />
        </GroupeDeReglages>
      </>
    ),
  } satisfies Record<OngletReglagesPaquet, unknown>;

  return (
    <Coquille titre="Réglages du paquet" colonne="formulaire">
      <div className="fc-ecran-reglages__entete">
        <h2 className="fc-fenetre__titre-ecran">{paquet.nom}</h2>
        <p className="fc-ecran-reglages__sous-titre">
          Paquet principal&nbsp;: ces réglages valent pour tous ses sous-paquets.
        </p>
      </div>
      <Onglets
        etiquette="Réglages du paquet"
        classeListe="fc-ecran-reglages__onglets"
        classePanneau="fc-ecran-reglages__panneau"
        onglets={[
          { valeur: 'seance', libelle: 'Séance' },
          { valeur: 'etapes', libelle: 'Étapes' },
          { valeur: 'planification', libelle: 'Planification' },
        ]}
        valeur={onglet}
        onChoisir={(o) => naviguer({ ecran: 'reglages-paquet', paquet: paquetId, onglet: o }, true)}
        panneau={panneaux[onglet]}
      />
    </Coquille>
  );
}
